import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { DEFAULT_ROLES } from '@conversed/shared/src/central/constants/permissions';
import { getInviteUserValidationSchema } from '@helpers/validationSchemaHelpers';
import { useModal } from '@hooks/modal/useModal';
import useRole from '@hooks/settings/roles/useRole';
import { useMainSelector } from '@hooks/useMainSelector';
import { useValidationForm } from '@hooks/useValidationForm';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInviteInboxes } from '../hooks/useInviteInboxes';
import { useInviteMember } from '../hooks/useInviteMember';
import { UserRoleSelect } from '../UserRoleSelect';
import { InviteInboxPicker } from './InviteInboxPicker';

type TInviteUserForm = {
  email: string;
  firstName: string;
  lastName: string;
  roleIds: string[];
  inboxes: string[];
};

// * LabelT keeps its default text-xs; the important modifier makes the
// * eyebrow size win over it.
const EYEBROW_LABEL_CLASSES =
  '!text-tiny leading-5 font-semibold uppercase tracking-wide text-gray-500';

const DEFAULT_VALUES: TInviteUserForm = {
  email: '',
  firstName: '',
  lastName: '',
  roleIds: [],
  inboxes: [],
};

export const InviteUserModal: React.FC = () => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const { inviteMember } = useInviteMember();
  const { assignableClientRoles } = useRole();
  const {
    user: { currentUser },
  } = useMainSelector();
  const isSuperAdmin = useMemo(() => {
    return currentUser?.isSuperAdmin === true;
  }, [currentUser]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // * ButtonT sets only aria-disabled, so a double click still submits. The
  // * state is stale inside the submit closure, so a ref guards re-entry.
  const isSubmitInFlightRef = useRef(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useValidationForm<TInviteUserForm>({
    schema: getInviteUserValidationSchema(),
    defaultValues: DEFAULT_VALUES,
  });

  const selectedRoleIds = watch('roleIds');
  const isInboxPickerVisible = useMemo(() => {
    if (!isSuperAdmin) {
      return false;
    }

    return selectedRoleIds.includes(DEFAULT_ROLES.LIVE_CHAT_AGENT_ROLE);
  }, [isSuperAdmin, selectedRoleIds]);
  const { loadState: inboxLoadState, retry: retryInboxLoad } =
    useInviteInboxes(isInboxPickerVisible);

  const changeRoleIds = (
    roleIds: string[],
    onFieldChange: (roleIds: string[]) => void,
  ) => {
    onFieldChange(roleIds);

    if (!roleIds.includes(DEFAULT_ROLES.LIVE_CHAT_AGENT_ROLE)) {
      setValue('inboxes', []);
    }
  };

  // * Only a superAdmin may set inboxes. The backend refuses them from anyone
  // * else, so a customer admin never sends the field.
  const getInboxesToSend = (inboxes: string[]): string[] | undefined => {
    if (!isSuperAdmin) {
      return undefined;
    }

    return inboxes;
  };

  const submitInvite = async (formValues: TInviteUserForm) => {
    isSubmitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      return await inviteMember({
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        roles: formValues.roleIds,
        inboxes: getInboxesToSend(formValues.inboxes),
      });
    } finally {
      isSubmitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (formValues: TInviteUserForm) => {
    if (isSubmitInFlightRef.current) {
      return;
    }

    const inviteResult = await submitInvite(formValues);

    if (inviteResult === 'invited') {
      hideModal();

      return;
    }

    if (inviteResult === 'already-member') {
      setError('email', { message: t('users.invite.already-member') });
    }
  };

  return (
    <form className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>
      <p className='text-xs leading-relaxed text-gray-600'>
        {t('users.invite.subtitle')}
      </p>

      <div className='grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2'>
        <div className='sm:col-span-2'>
          <InputWithLabelT
            type='email'
            field='email'
            id='invite-user-email'
            label={t('users.invite.email')}
            labelClassName={EYEBROW_LABEL_CLASSES}
            placeholder=''
            control={control}
            error={errors.email?.message}
          />
        </div>
        <div>
          <InputWithLabelT
            field='firstName'
            id='invite-user-first-name'
            label={t('users.invite.first-name')}
            labelClassName={EYEBROW_LABEL_CLASSES}
            placeholder=''
            control={control}
            error={errors.firstName?.message}
          />
        </div>
        <div>
          <InputWithLabelT
            field='lastName'
            id='invite-user-last-name'
            label={t('users.invite.last-name')}
            labelClassName={EYEBROW_LABEL_CLASSES}
            placeholder=''
            control={control}
            error={errors.lastName?.message}
          />
        </div>
        <div className='sm:col-span-2'>
          <LabelT
            text={t('users.invite.role')}
            htmlFor='invite-user-role'
            className={EYEBROW_LABEL_CLASSES}
          />
          <div className='mt-1'>
            <Controller
              name='roleIds'
              control={control}
              render={({ field }) => (
                <UserRoleSelect
                  id='invite-user-role'
                  variant='field'
                  roles={assignableClientRoles}
                  selectedRoleIds={field.value}
                  onChange={(roleIds) => changeRoleIds(roleIds, field.onChange)}
                  placeholder={t('users.invite.role-placeholder')}
                  hasError={Boolean(errors.roleIds)}
                />
              )}
            />
          </div>
          {errors.roleIds?.message && (
            <p className='mt-0.5 text-pico text-red-base'>
              {errors.roleIds.message}
            </p>
          )}
        </div>
        {isInboxPickerVisible && (
          <Controller
            name='inboxes'
            control={control}
            render={({ field }) => (
              <InviteInboxPicker
                loadState={inboxLoadState}
                onRetry={retryInboxLoad}
                selectedInboxIds={field.value}
                onChange={field.onChange}
                labelClassName={EYEBROW_LABEL_CLASSES}
              />
            )}
          />
        )}
      </div>

      <div className='flex justify-between border-t border-gray-200 pt-4'>
        <ButtonT
          type='button'
          styling='outline'
          size='xs'
          rounding='md'
          disabled={isSubmitting}
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='submit'
          styling='primary'
          size='xs'
          rounding='md'
          disabled={isSubmitting}
          className='flex items-center gap-2 px-8'>
          {isSubmitting && <Spinner size='sm' />}
          {isSubmitting
            ? t('users.invite.submitting')
            : t('users.invite.submit')}
        </ButtonT>
      </div>
    </form>
  );
};

export default InviteUserModal;
